import { css } from '@emotion/react';
import { useCareCreditLocationStatus, useCareCreditLocationProviderStatus } from '@frontend/payments-care-credit';
import { getPaymentsBillingManagerLocations, useBulkCollectionsFeature, useMerchants } from '@frontend/payments-hooks';
import { StripeSignupModal, useStripeOnboarding } from '@frontend/payments-setup';
import { useModalControl } from '@frontend/design-system';
import { BulkCollectionsFeature } from './atoms/bulk-collections-feature';
import { CareCreditFinancing } from './atoms/care-credit-financing';
import { SignupPageFooter } from './atoms/signup-page-footer';
import { SignupPageHeader } from './atoms/signup-page-header';
import { StripeCaseStudies } from './atoms/stripe-case-studies';
import { StripePaymentStatistics } from './atoms/stripe-payment-statistics';

const styles = {
  page: css`
    display: flex;
    flex-direction: column;
  `,
};

interface StripeSignupPageProps {
  locationIds: string[];
}

export const StripeSignupPage = ({ locationIds }: StripeSignupPageProps) => {
  const { isPaymentsBillingManager } = getPaymentsBillingManagerLocations({
    locationIds,
  });

  const {
    firstLocation: { activeProcessor, locationId, refetch },
  } = useMerchants(locationIds);

  const { modalProps, triggerProps } = useModalControl();

  const { onCreateAccountClick, onContinueOnboardClick } = useStripeOnboarding({
    locationId: locationId || '',
    onCreateError: modalProps.onClose,
    onRedirect: modalProps.onClose,
    refetch,
  });

  const { isBulkCollectionsEligible } = useBulkCollectionsFeature();

  const { isCareCreditEnabled } = useCareCreditLocationStatus(locationId);

  const { isProviderConnected, isProviderLoading } = useCareCreditLocationProviderStatus(
    { locationId },
    { enabled: isCareCreditEnabled }
  );

  const isCareCreditEligible = isCareCreditEnabled && !isProviderLoading && !isProviderConnected;

  return (
    <div css={styles.page}>
      <SignupPageHeader
        isPaymentsBillingManager={isPaymentsBillingManager}
        stripeId={activeProcessor?.stripeId}
        modalTriggerProps={triggerProps}
        onOnboardClick={onContinueOnboardClick}
      />
      <StripePaymentStatistics />
      <StripeCaseStudies />
      {isBulkCollectionsEligible && <BulkCollectionsFeature />}
      {!isBulkCollectionsEligible && isCareCreditEligible && <CareCreditFinancing />}
      <SignupPageFooter
        isPaymentsBillingManager={isPaymentsBillingManager}
        modalTriggerProps={triggerProps}
        stripeId={activeProcessor?.stripeId}
        onOnboardClick={onContinueOnboardClick}
        grayBg={!isBulkCollectionsEligible && !isCareCreditEligible} // only apply gray background if bulk collections feature is hidden, to visually separate from the case studies section
      />
      <StripeSignupModal modalProps={modalProps} onCreateAccount={onCreateAccountClick} />
    </div>
  );
};
